
type OffsetGetter = number | (() => number)

declare global {
  interface Window {
    __ANCHOR_SCROLL_OFFSET__?: OffsetGetter
  }
}

function getOffset(): number {
  const o = window.__ANCHOR_SCROLL_OFFSET__
  if (typeof o === 'function') {
    try {
      const v = o()
      if (Number.isFinite(v)) return v as number
    } catch {}
  } else if (typeof o === 'number' && Number.isFinite(o)) {
    return o
  }
  const fixed = document.querySelector('[data-fixed-header]') as HTMLElement | null
  if (fixed && fixed.offsetHeight) return fixed.offsetHeight
  return 80
}

function findAnchorTarget(hash: string): HTMLElement | null {
  if (!hash) return null
  const id = decodeURIComponent(hash.replace(/^#/, ''))
  if (!id) return null

  let el = document.getElementById(id)
  if (el) return el

  try {
    const esc =
      (window as any).CSS && (window as any).CSS.escape
        ? (window as any).CSS.escape(id)
        : id
    el = document.querySelector(`[name="${esc}"]`) as HTMLElement | null
  } catch {
    /* ignore */
  }

  return el
}

function scrollToHash(hash: string): boolean {
  const el = findAnchorTarget(hash)
  if (!el) return false

  const top = el.getBoundingClientRect().top + window.pageYOffset - getOffset()
  window.scrollTo({ top: Math.max(0, Math.round(top)), left: 0, behavior: 'smooth' })
  return true
}

// НЕ теряем текущий путь: всегда формируем URL из pathname + search + hash
function setUrlHash(hash: string, mode: 'push' | 'replace' = 'push') {
  if (!hash.startsWith('#')) hash = `#${hash}`
  const base = location.pathname + location.search
  const url = base + hash
  if (mode === 'push') history.pushState(null, document.title, url)
  else history.replaceState(null, document.title, url)
}

function getHashFromHref(a: HTMLAnchorElement): string | null {
  const raw = (a.getAttribute('href') || '').trim()
  if (!raw) return null

  // 1) Классический якорь на этой же странице
  if (raw.startsWith('#')) return raw

  // 2) Сломанный из-за <base href="/"> вариант "/#id" — принудительно считаем локальным якорем
  if (raw.startsWith('/#')) return raw.replace(/^\/#/, '#')

  // 3) Если у элемента есть a.hash (браузер его уже распарсил) и путь якоря совпадает с текущим — используем
  if (a.hash && (a.pathname === location.pathname || a.pathname === '/')) return a.hash

  return null
}

function onDocumentClick(e: MouseEvent) {
  const target = e.target as Element | null
  const a = (target && (target.closest ? target.closest('a') : null)) as HTMLAnchorElement | null
  if (!a) return

  // модификаторы/не ЛКМ/target=_blank — пропускаем
  if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
  if (a.getAttribute('target') === '_blank') return

  const hash = getHashFromHref(a)
  if (!hash) return // это не локальный якорь — не трогаем

  // не даём роутеру/браузеру увести на '/#id'
  e.preventDefault()

  const deadline = Date.now() + 800
  const tryScroll = () => {
    const ok = scrollToHash(hash)
    if (ok || Date.now() > deadline) {
      // по умолчанию создаём запись в истории (как обычный клик по якорю)
      setUrlHash(hash, 'push')
    } else {
      requestAnimationFrame(tryScroll)
    }
  }
  requestAnimationFrame(tryScroll)
}

function onHashChange() {
  if (!location.hash) return
  requestAnimationFrame(() => scrollToHash(location.hash))
}

function onLoad() {
  if (!location.hash) return
  requestAnimationFrame(() => scrollToHash(location.hash))
}

export default () => {
  if (process.server) return
  document.addEventListener('click', onDocumentClick, true)
  window.addEventListener('hashchange', onHashChange)
  window.addEventListener('load', onLoad)
}
